import axios from 'axios';

const API_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://blockweb-ai-8d834f26f7dc.herokuapp.com';

function normalizeIdShape(item) {
  if (!item || typeof item !== 'object') {
    return item;
  }

  if (Array.isArray(item)) {
    return item.map(normalizeIdShape);
  }

  const normalized = { ...item };
  if (!normalized._id && normalized.id) {
    normalized._id = normalized.id;
  }
  return normalized;
}

function withProfileId(profileId) {
  return profileId ? { params: { profile_id: profileId } } : {};
}

async function apiCall(method, url, data = null, customConfig = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...(customConfig.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await axios({
      method,
      url: `${API_URL}${url}`,
      data,
      ...customConfig,
      headers,
    });
    return response.data;
  } catch (error) {
    console.error(error.response?.data?.detail || error.message);
    throw error;
  }
}

export function extractApiError(error, fallback = 'Something went wrong.') {
  return error?.response?.data?.detail || error?.response?.data?.message || error?.message || fallback;
}

export async function getPrompts(profileId) {
  const data = await apiCall('get', '/prompts', null, withProfileId(profileId));
  return normalizeIdShape(data);
}

export async function createPrompt(text, order, profileId) {
  const data = await apiCall('post', '/prompts', { text, order }, withProfileId(profileId));
  return normalizeIdShape(data);
}

export async function updatePrompt(id, text, order) {
  const data = await apiCall('put', `/prompts/${id}`, { text, order });
  return normalizeIdShape(data);
}

export async function deletePrompt(id) {
  return apiCall('delete', `/prompts/${id}`);
}

export async function setCurrentPrompt(id, text) {
  return apiCall('post', '/current-prompt', { id, text });
}

export async function getCurrentPrompt() {
  return apiCall('get', '/current-prompt');
}

export async function getProfiles() {
  const data = await apiCall('get', '/user-profiles');
  return normalizeIdShape(data);
}

export async function getActiveClassroomSchedules() {
  return apiCall('get', '/organization/classroom-schedules/active');
}

export async function getTeacherOverview() {
  return apiCall('get', '/teacher/overview');
}

export async function createClassroomSchedule(profileId, payload) {
  return apiCall('post', `/profiles/${profileId}/classroom-schedules`, payload);
}

export async function updateClassroomSchedule(profileId, scheduleId, payload) {
  return apiCall('put', `/profiles/${profileId}/classroom-schedules/${scheduleId}`, payload);
}

export async function deleteClassroomSchedule(profileId, scheduleId) {
  return apiCall('delete', `/profiles/${profileId}/classroom-schedules/${scheduleId}`);
}

export async function getOrganization() {
  return apiCall('get', '/organization/me');
}

export async function updateOrganization(name) {
  return apiCall('put', '/organization/me', name);
}

export async function getRosteringIntegration() {
  return apiCall('get', '/organization/rostering/integration');
}

export async function updateRosteringIntegration(payload) {
  return apiCall('put', '/organization/rostering/integration', payload);
}

export async function syncRosteringIntegration(payload = {}) {
  return apiCall('post', '/organization/rostering/sync', payload);
}

export async function getOrganizationMembers() {
  return apiCall('get', '/organization/members');
}

export async function addOrganizationMember(email, role) {
  return apiCall('post', '/organization/members', { email, role });
}

export async function updateOrganizationMember(memberId, role) {
  return apiCall('put', `/organization/members/${memberId}`, { role });
}

export async function removeOrganizationMember(memberId) {
  return apiCall('delete', `/organization/members/${memberId}`);
}

export async function transferOrganizationOwnership(memberId) {
  return apiCall('post', '/organization/transfer-ownership', { member_id: memberId });
}

export async function leaveOrganization() {
  return apiCall('post', '/organization/leave');
}

export async function getOrganizationAuditLogs(limit = 100) {
  return apiCall('get', '/organization/audit-logs', null, { params: { limit } });
}

export async function getOrganizationInvitations() {
  return apiCall('get', '/organization/invitations');
}

export async function getManagedDevices() {
  return apiCall('get', '/organization/devices');
}

export async function createManagedDeviceEnrollment(payload) {
  return apiCall('post', '/organization/devices/enrollment-codes', payload);
}

export async function importManagedDeviceRoster(payload) {
  return apiCall('post', '/organization/devices/import-roster', payload);
}

export async function revokeManagedDevice(deviceId) {
  return apiCall('delete', `/organization/devices/${deviceId}`);
}

export async function updateManagedDevice(deviceId, payload) {
  return apiCall('put', `/organization/devices/${deviceId}`, payload);
}

export async function rotateManagedDeviceToken(deviceId) {
  return apiCall('post', `/organization/devices/${deviceId}/rotate-token`);
}

export async function bulkManageDevices(payload) {
  return apiCall('post', '/organization/devices/bulk-actions', payload);
}

export async function runOrganizationCleanup(payload) {
  return apiCall('post', '/organization/cleanup', payload);
}

export async function normalizeDuplicateUserEmails() {
  return apiCall('post', '/admin/cleanup/duplicate-user-emails');
}

export async function getAdminStats() {
  return apiCall('get', '/admin/stats');
}

export async function getAdminFeedback() {
  return apiCall('get', '/admin/beta-feedback');
}

export async function getAdminUsers() {
  return apiCall('get', '/admin/users');
}

export async function getOrganizationInvitationDetails(token) {
  return apiCall('get', `/organization/invitations/${token}`);
}

export async function createOrganizationInvitation(email, role) {
  return apiCall('post', '/organization/invitations', { email, role });
}

export async function revokeOrganizationInvitation(invitationId) {
  return apiCall('delete', `/organization/invitations/${invitationId}`);
}

export async function acceptOrganizationInvitation(token) {
  return apiCall('post', `/organization/invitations/${token}/accept`);
}

export async function getOrganizationReportSummary(days = 30) {
  return apiCall('get', '/organization/reports/summary', null, { params: { days } });
}

export async function getOrganizationOpsSummary() {
  return apiCall('get', '/organization/ops-summary');
}

export async function sendOrganizationSmtpTest(payload = {}) {
  return apiCall('post', '/organization/smtp-test', payload);
}

export async function getOrganizationReportEvents(params = {}) {
  return apiCall('get', '/organization/reports/events', null, { params });
}

async function downloadCsv(url, filename, params = {}) {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_URL}${url}`, {
    params,
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    },
  });

  const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

async function downloadJson(url, filename, params = {}) {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_URL}${url}`, {
    params,
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    },
  });

  const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export async function downloadOrganizationReportCsv(days = 30) {
  return downloadCsv('/organization/reports/export.csv', `blockwebai-school-report-${days}d.csv`, { days });
}

export async function downloadOrganizationAuditCsv(limit = 5000) {
  return downloadCsv('/organization/audit-logs/export.csv', 'blockwebai-audit-log.csv', { limit });
}

export async function downloadOrganizationSupportBundle() {
  return downloadJson('/organization/support-bundle.json', 'blockwebai-support-bundle.json');
}

export async function logExtensionEvent(payload) {
  return apiCall('post', '/organization/extension-events', payload);
}

export async function createProfile(profileName) {
  return apiCall('post', '/create-profile', { profile_name: profileName });
}

export async function cloneProfile(profileId, profileName) {
  return apiCall('post', `/profiles/${profileId}/clone`, { profile_name: profileName });
}

export async function deleteProfile(profileId) {
  return apiCall('delete', `/delete-profile/${profileId}`);
}

export async function getProfileData(profileId) {
  return apiCall('get', `/profile-data/${profileId}`);
}

export async function updateProfileData(profileId, prompts, whitelist, blacklist) {
  return apiCall('put', `/profile-data/${profileId}`, { prompts, whitelist, blacklist });
}

export async function getUniversalData() {
  return apiCall('get', '/universal-data');
}

export async function updateUniversalData(data) {
  return apiCall('put', '/universal-data', data);
}

export async function getWhitelist(profileId) {
  const data = await apiCall('get', '/whitelist', null, withProfileId(profileId));
  return normalizeIdShape(data);
}

export async function addToWhitelist(url, profileId) {
  return apiCall('post', '/whitelist', { url }, withProfileId(profileId));
}

export async function removeFromWhitelist(id) {
  return apiCall('delete', `/whitelist/${id}`);
}

export async function updateWhitelist(id, url, order) {
  return apiCall('put', `/whitelist/${id}`, { url, order });
}

export async function getBlacklist(profileId) {
  const data = await apiCall('get', '/blacklist', null, withProfileId(profileId));
  return normalizeIdShape(data);
}

export async function addToBlacklist(url, profileId) {
  return apiCall('post', '/blacklist', { url }, withProfileId(profileId));
}

export async function removeFromBlacklist(id) {
  return apiCall('delete', `/blacklist/${id}`);
}

export async function updateBlacklist(id, url, order) {
  return apiCall('put', `/blacklist/${id}`, { url, order });
}

export async function getTemporaryOverrides(profileId) {
  return apiCall('get', '/temporary-overrides', null, withProfileId(profileId));
}

export async function createTemporaryOverride(payload, profileId) {
  return apiCall('post', '/temporary-overrides', payload, withProfileId(profileId));
}

export async function deleteTemporaryOverride(id) {
  return apiCall('delete', `/temporary-overrides/${id}`);
}

export async function registerUser(payload) {
  return apiCall('post', '/users/', payload);
}

export async function loginUser(username, password) {
  try {
    const response = await axios.post(
      `${API_URL}/token`,
      `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    localStorage.setItem('token', response.data.access_token);
    return response.data;
  } catch (error) {
    console.error(error.response?.data?.detail || error.message);
    throw error;
  }
}

export async function loginWithGoogle(payload) {
  try {
    const response = await axios.post(`${API_URL}/auth/google`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    localStorage.setItem('token', response.data.access_token);
    return response.data;
  } catch (error) {
    console.error(error.response?.data?.detail || error.message);
    throw error;
  }
}

export async function getUser() {
  return apiCall('get', '/users/me');
}

export async function addWhitelistBulk(urls) {
  return apiCall('post', '/whitelist/bulk', urls);
}

export async function addBlacklistBulk(urls) {
  return apiCall('post', '/blacklist/bulk', urls);
}

export async function sendContactForm(name, email, message) {
  return apiCall('post', '/contact', { name, email, message });
}

export async function submitBetaFeedback(payload) {
  return apiCall('post', '/beta-feedback', payload);
}

export async function runPublicDemoAnalysis(payload) {
  const response = await axios.post(`${API_URL}/demo/analyze`, payload, {
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
  });
  return response.data;
}

export async function getRunsRemaining() {
  return apiCall('get', '/runs-remaining');
}

export async function updateSubscription(subscriptionType) {
  return apiCall('post', '/set-subscription', { subscription_type: subscriptionType });
}
